import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocDiscardEvent } from '@common/collab/events/collab-doc-discard.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { CollabDocSerializationError } from '@common/collab/types/collab-doc-serialization-error.type';
import { DocTimelineUpdate } from '@common/collab/types/doc-timeline-update.type';
import { stepsAsJSON } from '@common/prosemirror/collab/step';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { ProseMirrorNodeJSON } from '@common/prosemirror/model/json';
import { TransactionMeta } from '@common/prosemirror/operations/operation-applier.type';
import { operationPluginKey } from '@common/prosemirror/operations/operation.plugin';
import { parseOperations } from '@common/prosemirror/operations/operations';
import { collabUsersPlugin, hasSendableCollabUser, receiveCollabUserTransaction } from '@common/prosemirror/plugins/collab-users.plugin';
import { transactionIdPlugin } from '@common/prosemirror/plugins/transaction-id.plugin';
import { CollabStep } from '@common/prosemirror/transform/collab-step.type';
import { CollabSessionError } from '@common/sockets/enums/collab-session-error.enum';
import { CollabSessionSaveResult } from '@common/sockets/enums/collab-session-save-result.enum';
import { CollabSessionSyncState } from '@common/sockets/enums/collab-session-sync-state.enum';
import { DocumentStateChangeReason } from '@common/sockets/enums/document-state-change-reason.enum';
import { SocketSessionError } from '@common/sockets/enums/socket-session-error.enum';
import { DocGetRequest, DocGetResponse } from '@common/sockets/models/doc-get.model';
import { DocRevertRequest } from '@common/sockets/models/doc-revert.model';
import { DocUpdateRequest, DocUpdateResponse } from '@common/sockets/models/doc-update.model';
import { DocUpdatesRequest, DocUpdatesResponse } from '@common/sockets/models/doc-updates.model';
import { DocUserUpdateRequest } from '@common/sockets/models/doc-user-update.model';
import { SocketEmitterError } from '@common/sockets/socket-emitter';
import { SessionOptions, SocketSession } from '@common/sockets/socket-session';
import { CollabSessionErrorEvent } from '@common/sockets/types/collab-session-error-event.type';
import { DocumentStateChange } from '@common/sockets/types/document-state-change.type';
import { CustomError } from '@common/util/custom-error';
import { isEnumValue } from '@common/util/enum';
import { PropertyObservable } from '@common/util/property-observable.decorator';
import { byteSize } from '@common/util/utf';
import { throttle } from 'lodash';
import { collab, getVersion, receiveTransaction, SendableSteps, sendableSteps } from 'prosemirror-collab';
import { EditorState, Plugin, Transaction } from 'prosemirror-state';
import { Mapping, Step } from 'prosemirror-transform';
import { Observable, Subject } from 'rxjs';
import { ManagerOptions, Socket, SocketOptions } from 'socket.io-client';

export interface CollabSessionOptions extends SessionOptions {
  branchName: string;
  canReceiveUpdates?: boolean;
  changeThrottleTimeMS?: number;
  clientId?: string;
  /** Used for creating DOM decorations if provided. Defaults to window.document. */
  document?: Document;
  editorState?: EditorState;
  filePath: string;
  fileType: CollabFileType;
  getTimelineDocVersion?: (docId: string) => number;
  plugins?: Plugin[];
  projectId: number;
  /** Used in a NodeJS environment to support self-signed certs locally. Only used by collab tests at this time. */
  rejectUnauthorizedCerts?: boolean;
  reviewFileId: number;
  schema: CollabSchema;
  socketMaxHttpBufferSizeBytes?: number;
  /**
   * Use true if the editor uses operations to update the prosemirror document,
   * this will cause the collab session to patch unconfirmed operations.
   * Defaults to false.
   */
  supportOperations?: boolean;
  timelineEnabled?: boolean;
  /**
   * Whether or not collab user data is sent between the collab session and the collab authority.
   * Eg the user's cursor position.
   * Defaults to true.
   */
  userDataEnabled?: boolean;
  userId: string;
  userName: string;
}

export interface CollabSessionTransactionTooLargeEvent {
  maxSize: number;
  transaction: Transaction;
  updateSize: number;
}

export interface CollabSessionUpdateEvent {
  session: CollabSession;
  /** The transaction that caused the update. Undefined if the update is from the initial doc load. */
  transaction: Transaction | undefined;
  change: DocumentStateChange;
}

const collabPluginKey = 'collab$';

/**
 * CollabSession
 */
export class CollabSession extends SocketSession<CollabSessionOptions, CollabSessionErrorEvent> {
  branchName: string;
  clientId: string;
  document: Document;
  editorState: EditorState;
  filePath: string;
  fileType: CollabFileType;
  getTimelineDocVersion: (docId: string) => number;
  plugins: Plugin[];
  projectId: number;
  reviewFileId: number;
  schema: CollabSchema;
  socketMaxHttpBufferSizeBytes: number;
  supportOperations: boolean;
  timelineEnabled: boolean;
  userDataEnabled: boolean;
  userId: string;
  userName: string;
  docCreatedAt?: ISO8601DateString;

  public docId: string;
  socket: Socket;

  /**
   * Whether or not the collab session is allowed to accept updates from the authority.
   * This is not set internally by `CollabSession` but is set by the editor.
   * For example, while a code editor has invalid markup it can set `canReceiveUpdates` to `false` until its markup is valid again and able to receive updates.
   * When set back to `true` the collab session automatically fetches missed updates from the authority.
   */
  public get canReceiveUpdates(): boolean {
    return this._canReceiveUpdates;
  }

  public set canReceiveUpdates(value: boolean) {
    if (value === this._canReceiveUpdates)
      return;
    if (value) {
      this._canReceiveUpdates = true;
      if (this.hasMissedUpdates) {
        this.fetchUpdates();
      }
      this.hasMissedUpdates = false;
    } else {
      this._canReceiveUpdates = false;
      this.hasMissedUpdates = false;
    }
  }

  private _canReceiveUpdates: boolean;
  private hasMissedUpdates: boolean = false;

  loaded: boolean = false;
  @PropertyObservable('loaded') loaded$: Observable<boolean>;

  private throttledSendChanges: Function;
  private throttledSendUserData: Function;

  /** Wether the doc when serialized to an xml string is larger than the max size for a doc. */
  exceedsMaxSize: boolean;
  @PropertyObservable('exceedsMaxSize') exceedsMaxSize$: Observable<boolean>;

  /** Wether the doc when saved to MongoDB is larger than the max size for a document in MongoDB. */
  exceedsMongoDBMaxSize: boolean;
  @PropertyObservable('exceedsMongoDBMaxSize') exceedsMongoDBMaxSize$: Observable<boolean>;

  /** Whether or not the session is in read-only mode. */
  readOnly: boolean = false;
  @PropertyObservable('readOnly') readOnly$: Observable<boolean>;

  /** Emits when a doc is committed to Git. */
  public docCommit$: Subject<CollabDocCommitEvent> = new Subject<CollabDocCommitEvent>();

  /** Emits when a doc is deleted. */
  public docDelete$: Subject<CollabDocDeleteEvent> = new Subject<CollabDocDeleteEvent>();

  /** Emits when a doc is discarded. */
  public docDiscard$: Subject<CollabDocDiscardEvent> = new Subject<CollabDocDiscardEvent>();

  /** Emits when a doc is merged. */
  public docMerge$: Subject<CollabDocMergeEvent> = new Subject<CollabDocMergeEvent>();

  /** Emits when a doc is renamed. */
  public docRename$: Subject<CollabDocRenameEvent> = new Subject<CollabDocRenameEvent>();

  /** Emits when a doc is restored to a previous version. */
  public docRestore$: Subject<CollabDocRestoreEvent> = new Subject<CollabDocRestoreEvent>();

  /** Emits when a timeline update is received from the authority. */
  public timelineUpdate$: Subject<DocTimelineUpdate> = new Subject<DocTimelineUpdate>();

  /** Emits when a users update is received from the authority. */
  public usersUpdate$: Subject<CollabUserJSON[]> = new Subject<CollabUserJSON[]>();

  /** The size of the doc when it is serialized to an xml string. */
  docSize: number;
  @PropertyObservable('docSize') docSize$: Observable<number>;

  /** The last time the doc was committed. */
  docCommittedAt: ISO8601DateString | undefined;
  @PropertyObservable('docCommittedAt') docCommittedAt$: Observable<ISO8601DateString>;

  /** The last time the doc was modified. */
  docModifiedAt: ISO8601DateString | undefined;
  @PropertyObservable('docModifiedAt') docModifiedAt$: Observable<ISO8601DateString>;

  public get docVersion(): number {
    return this.loaded ? getVersion(this.editorState) : undefined;
  }

  /** The current syncing state of the session. */
  get syncState(): CollabSessionSyncState {
    return this._syncState;
  }
  set syncState(syncState: CollabSessionSyncState) {
    if (this._syncState === CollabSessionSyncState.Stopped && syncState !== CollabSessionSyncState.Stopped) {
      throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
    }

    // If the session is reverting then only allow the sync state to change to Stopped, Reverted, or RevertFailed
    // Reverting is a special case because the reversion has priority over loading and saving states.
    if (this._syncState === CollabSessionSyncState.Reverting) {
      if (syncState !== CollabSessionSyncState.Stopped && syncState !== CollabSessionSyncState.Reverted && syncState !== CollabSessionSyncState.RevertFailed) {
        return;
      }
    }

    if (syncState === CollabSessionSyncState.Saved) {
      if (this._syncState !== CollabSessionSyncState.Saving) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    } else if (syncState === CollabSessionSyncState.Loaded) {
      if (this._syncState !== CollabSessionSyncState.Loading) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    } else if (syncState === CollabSessionSyncState.SaveFailed) {
      if (this._syncState !== CollabSessionSyncState.Saving) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    } else if (syncState === CollabSessionSyncState.Unsaved) {
      if (this._syncState !== CollabSessionSyncState.Loaded && this._syncState !== CollabSessionSyncState.Saved && this._syncState !== CollabSessionSyncState.Saving && this._syncState !== CollabSessionSyncState.SaveFailed && this._syncState !== CollabSessionSyncState.Unsaved && this._syncState !== CollabSessionSyncState.RevertFailed) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    } else if (syncState === CollabSessionSyncState.Saving) {
      if (this._syncState !== CollabSessionSyncState.Loaded && this._syncState !== CollabSessionSyncState.Saved && this._syncState !== CollabSessionSyncState.SaveFailed && this._syncState !== CollabSessionSyncState.Unsaved) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    } else if (syncState === CollabSessionSyncState.Loading) {
      if (this._syncState !== CollabSessionSyncState.NotLoaded) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    } else if (syncState === CollabSessionSyncState.Reverted) {
      if (this._syncState !== CollabSessionSyncState.Reverting) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    } else if (syncState === CollabSessionSyncState.RevertFailed) {
      if (this._syncState !== CollabSessionSyncState.Reverting) {
        throw new CustomError('CollabSessionInvalidSyncStateTransition', `${this._syncState} -> ${syncState}`);
      }
    }

    this._syncState = syncState;
  }
  private _syncState: CollabSessionSyncState = CollabSessionSyncState.NotLoaded;
  @PropertyObservable('syncState') syncState$: Observable<CollabSessionSyncState>;

  /**
   * Whether or not the session is offline. The session does not determine if it is offline or not.
   * This should be set whenever the client's offline state changes such as as browser's offline mode being toggled.
   */
  offline: boolean = false;
  @PropertyObservable('offline') offline$: Observable<boolean>;

  constructor(options: CollabSessionOptions) {
    super(options);

    this.branchName = options.branchName ?? '';
    this.canReceiveUpdates = options.canReceiveUpdates ?? true;
    this.clientId = options.clientId;
    this.document = options.document;
    this.editorState = options.editorState;
    this.fileType = options.fileType;
    this.filePath = options.filePath ?? '';
    this.getTimelineDocVersion = options.getTimelineDocVersion;
    this.offline = options.offline ?? false;
    this.plugins = options.plugins ?? [];
    this.projectId = options.projectId ?? null;
    this.reviewFileId = options.reviewFileId ?? null;
    this.schema = options.schema;
    this.socketMaxHttpBufferSizeBytes = options.socketMaxHttpBufferSizeBytes;
    this.supportOperations = options.supportOperations ?? false;
    this.timelineEnabled = options.timelineEnabled ?? false;
    this.userDataEnabled = options.userDataEnabled ?? true;
    this.userId = options.userId;
    this.userName = options.userName;

    // Created a throttled version of sendChanges so that the updates sent to the server don't happen too often
    this.throttledSendChanges = throttle(() => {
      // Use setTimeout to always run this function in the next event cycle. Its possible for sendChanges to call throttledSendChanges and we only want one sendChanges running at a time.
      setTimeout(() => {
        try {
          // Because this function is scheduled to happen in the future its possible that the session has been stopped since then
          // So do a check to make sure the changes can and should be sent.
          if (this.stopped || !this.emitter || !this.editorState) {
            return;
          }

          const stepsInfo = sendableSteps(this.editorState);
          if (stepsInfo) {
            this.sendChanges(this.editorState, stepsInfo).catch(error => {
              // Do nothing.
              // This error has already been handled by onServerError but it still needs to be caught so that it is not an unhandled promise error
            });
          }
        } catch (error) {
          this.error = { type: CollabSessionError.ThrottledSendChangesFailed, error };
        }
      });
    }, options.changeThrottleTimeMS ?? 1000, {
      leading: false
    });

    // Created a throttled version of sendUserData so that the updates sent to the server don't happen too often
    this.throttledSendUserData = throttle(() => {
      // Use setTimeout to always run this function in the next event cycle. Its possible for sendChanges to call throttledSendChanges and we only want one sendChanges running at a time.
      setTimeout(() => {
        try {
          // Because this function is scheduled to happen in the future its possible that the session has been stopped since then
          // So do a check to make sure the data can and should be sent.
          if (this.stopped || !this.emitter || !this.editorState) {
            return;
          }

          if (this.hasSendableCollabUserData(this.editorState)) {
            this.sendUserData();
          }
        } catch (error) {
          this.error = { type: CollabSessionError.ThrottledSendUserDataFailed, error };
        }
      });
    }, options.changeThrottleTimeMS ?? 1000, {
      leading: false
    });
  }

  protected override createSocketOptions(socketUrl: URL): Partial<ManagerOptions & SocketOptions> {
    return {
      ...super.createSocketOptions(socketUrl),
      query: {
        branchName: this.branchName,
        clientId: this.clientId,
        filePath: this.filePath,
        fileType: this.fileType,
        projectId: this.projectId,
        reviewFileId: Number.isInteger(this.reviewFileId) ? this.reviewFileId.toString() : '',
        schemaName: this.schema.name,
        schemaVersion: this.schema.version,
        userId: this.userId
      }
    }
  }

  protected override parseError(event: string, error: any): CollabSessionErrorEvent {
    if ((event === 'connect_error' || event === 'reconnect_error') &&
      error.data && (isEnumValue(SocketSessionError, error.data.name) || isEnumValue(CollabSessionError, error.data.name))) {
      return { type: error.data.name as CollabSessionError, error: error.data };
    }

    return { type: SocketSessionError.UnableToReachServer, error };
  }

  /** Creates a session with the server by opening a socket connection and setting up event handlers. */
  public override start() {
    super.start();

    // Listen to the ready event and load the document
    this.socket.on('ready', data => {
      // Store the docId for future communication with the collab server
      this.docId = data.docId;

      // If the doc is already loaded then fetch the updates for it
      if (this.loaded) {
        // Send the user's data since it would have been removed on disconnect
        this.sendUserData(false);
        this.fetchUpdates();
        // Else load the entire doc
      } else {
        this.loadDoc();
      }
    });

    // Listen to incompatible schema errors
    this.socket.on('doc.schema.incompatible', error => {
      this.onServerError(error);
    });

    // Listen to doc changed events in order to get the latest updates to the doc
    this.socket.on('doc.changed', data => {
      if (this.loaded) {
        this.fetchUpdates();
      }
    });

    // Listen to doc committed events in order to emit the event and to get the latest updates to the doc
    this.socket.on('doc.committed', data => {
      if (this.loaded) {
        this.fetchUpdates();
      }
      this.docCommit$.next(data);
    });

    // Listen to doc deleted events in order to emit the event
    this.socket.on('doc.deleted', data => {
      this.docDelete$.next(data);
    });

    // Listen to doc discarded events in order to emit the event
    this.socket.on('doc.discarded', data => {
      this.docDiscard$.next(data);
      // The doc's changes have been discarded so reload the doc to make sure the latest version is loaded
      this.loadDoc();
    });

    // Listen to doc merged events in order to emit the event
    this.socket.on('doc.merged', data => {
      this.docMerge$.next(data);
    });

    // Listen to doc renamed events in order to emit the event
    this.socket.on('doc.renamed', data => {
      this.docRename$.next(data);
    });

    // Listen to doc restored events in order to emit the event
    this.socket.on('doc.restored', data => {
      this.docRestore$.next(data);
    });

    // Listen to doc readOnly changed events in order to update the readOnly state
    this.socket.on('doc.readOnly.changed', data => {
      this.readOnly = data.readOnly;
    });
  }

  protected override onStopped() {
    this.loaded = false;
    this.editorState = null;
    this.syncState = CollabSessionSyncState.Stopped;
  }

  /** Saves the local doc changes to the server. */
  save(): Promise<CollabSessionSaveResult> {
    return new Promise<CollabSessionSaveResult>((resolve, reject) => {
      // Because this function is designed to be called externally its possible that the session has been stopped so do a check to make sure the changes can and should be sent.
      if (this.stopped || !this.emitter) {
        // Consider this a successful save
        resolve(CollabSessionSaveResult.SessionStopped);
        return;
      }

      const stepsInfo = sendableSteps(this.editorState);

      // If there are steps to save then send the changes to the server
      if (stepsInfo) {
        this.sendChanges(this.editorState, stepsInfo).then(resolve, reject);
      } else {
        // Else there are no changes so consider this a successful save
        resolve(CollabSessionSaveResult.NothingToSave);
      }
    });
  }

  revertDocToSavedState(): Promise<void> {
    this.syncState = CollabSessionSyncState.Reverting;

    return this.emitter.emit<DocRevertRequest>('doc.revert', {
      docId: this.docId
    }, this.retryEmit.bind(this)).then(() => {
      this.syncState = CollabSessionSyncState.Reverted;
    }).catch(error => {
      this.syncState = CollabSessionSyncState.RevertFailed;
      this.onServerError(error);
    });
  }

  /** Dispatches a local transaction to the prosemirror editor.
   * Returns `true` if the transaction was dispatched. */
  dispatch(transaction: Transaction): boolean {
    return this.runDispatch(transaction, true, DocumentStateChangeReason.Editor);
  }

  /** Dispatches a collab transaction to the prosemirror editor and optionally sends any changes to the server. */
  private dispatchCollabTransaction(transaction: Transaction, checkForChanges: boolean) {
    this.runDispatch(transaction, checkForChanges, DocumentStateChangeReason.Collab);
  }

  /** Dispatches a transaction to the prosemirror editor and optionally sends any changes to the server.
   * Returns `true` if the transaction was dispatched. */
  private runDispatch(transaction: Transaction, checkForChanges: boolean, reason: DocumentStateChangeReason): boolean {
    // Do not allow changes if the collab session has been stopped
    if (this.stopped) {
      return false;
    }

    // Do not allow changes if there is no connection to the server
    // OR the session has been stopped
    // OR the doc has not been successfully loaded from the server
    // OR the doc is reverting
    if (this.offline || !this.connected || !this.loaded || this.syncState === CollabSessionSyncState.Reverting) {
      // If there is no connection then try reconnecting right now
      if (!this.connected) {
        this.socket.connect();
      } else if (!this.loaded) {
        // Else if the doc has not been loaded yet try again
        this.loadDoc();
      }

      return false;
    }

    // Do not allow transactions if they change the doc when in readonly mode.
    // We want to keep transactions that change the selection so the user can still select content in the doc.
    // We also want to allow passive transactions like meta data loading.
    if (this.readOnly && transaction.docChanged) {
      return false;
    }

    // Apply the transaction
    const result = this.editorState.applyTransaction(transaction);
    // No need to continue if the transaction was not applied.
    if (!result.transactions.length)
      return false;
    const newState = result.state;

    // Ignore the transaction if it is too large to send over the socket connection
    if (typeof this.socketMaxHttpBufferSizeBytes === 'number') {
      const stepsInfo = sendableSteps(newState);
      if (stepsInfo) {
        const update = this.createUpdate(newState, stepsInfo);
        const updateSize = byteSize(JSON.stringify(update));

        // If the update size is greater than 1MB then ignore the transaction (add 5120 to account for the message overhead)
        if (updateSize + 5120 > this.socketMaxHttpBufferSizeBytes) {
          this.emit('transactionTooLarge', {
            maxSize: this.socketMaxHttpBufferSizeBytes,
            transaction,
            updateSize
          });
          return false;
        }
      }
    }

    // If there is new state after the transaction then check if there are more changes to send to the server
    if (newState) {
      this.editorState = newState;
      if (checkForChanges) {
        this.checkForAndSendChanges();
      }
    }

    // Apply the state to the editor
    if (this.editorState) {
      const change: DocumentStateChange = {
        state: this.editorState,
        reason: reason,
        changed: transaction.docChanged
      };
      this.emit('update', {
        session: this,
        transaction,
        change
      });
      return true;
    }

    return false;
  }

  /** Checks if there are unsaved changes and sends them to the server if there are. */
  private checkForAndSendChanges(): boolean {
    // If there are unsaved changes then send them to the server
    if (sendableSteps(this.editorState)) {
      // If saving then don't change the sync state
      if (this.syncState !== CollabSessionSyncState.Saving) {
        this.syncState = CollabSessionSyncState.Unsaved;

        // Send the changes to the collab server
        this.throttledSendChanges();
      }

      return true;
    } else {
      // There weren't any doc changes to send but there might be user data to send (eg cursor position)
      if (this.hasSendableCollabUserData(this.editorState)) {
        this.throttledSendUserData();
      }

      // If the sync state is currently a failed save when we have nothing to save
      if (this.syncState === CollabSessionSyncState.SaveFailed) {
        // Then the save must have been successful but the session didn't get a response from the server saying as much
        // Set the sync state to Saved by first going through Saving
        this.syncState = CollabSessionSyncState.Saving;
        this.syncState = CollabSessionSyncState.Saved;
      }
    }

    return false;
  }

  /** Sends the local doc changes to the server. */
  private sendChanges(state: EditorState, stepsInfo: SendableSteps): Promise<CollabSessionSaveResult> {
    if (this.syncState === CollabSessionSyncState.Stopped) {
      return Promise.resolve(CollabSessionSaveResult.SessionStopped);
    } else if (this.syncState === CollabSessionSyncState.NotLoaded || this.syncState === CollabSessionSyncState.Loading) {
      return Promise.resolve(CollabSessionSaveResult.NotLoaded);
    } else if (this.syncState === CollabSessionSyncState.Saving) {
      return Promise.resolve(CollabSessionSaveResult.AlreadySaving);
    } else if (this._syncState === CollabSessionSyncState.Loaded || this._syncState === CollabSessionSyncState.Saved || this._syncState === CollabSessionSyncState.SaveFailed || this._syncState === CollabSessionSyncState.Unsaved) {
      if (!stepsInfo || !Array.isArray(stepsInfo.steps) || stepsInfo.steps.length === 0) {
        return Promise.resolve(CollabSessionSaveResult.NothingToSave);
      } else {
        this.syncState = CollabSessionSyncState.Saving;

        try {
          const docUpdate = this.createUpdate(state, stepsInfo);
          return this.emitter.emit<DocUpdateRequest, DocUpdateResponse>('doc.update', docUpdate, this.retryEmit.bind(this)).then(result => {
            // The session may have stopped while this emit was in flight — editorState is
            // nulled by then, so don't touch it or move off the terminal Stopped state.
            if (this.syncState === CollabSessionSyncState.Stopped) {
              return CollabSessionSaveResult.SessionStopped;
            }

            const tr = receiveTransaction(this.editorState, stepsInfo.steps, Array.isArray(stepsInfo.steps) ? stepsInfo.steps.map(() => stepsInfo.clientID) : [], { mapSelectionBackward: true });
            this.receiveCollabUserTransaction(tr, this.editorState, [docUpdate.collabUser], { action: 'add' });
            this.dispatchCollabTransaction(tr, false);
            this.syncState = CollabSessionSyncState.Saved;
            const hasUnsavedChanges = this.checkForAndSendChanges();

            if (hasUnsavedChanges) {
              return CollabSessionSaveResult.MoreToSave;
            } else {
              return CollabSessionSaveResult.Saved;
            }
          }).catch(error => {
            // Update the sync state
            if (error.name === SocketEmitterError.AlreadyEmittingMessage) {
              return CollabSessionSaveResult.AlreadySaving;
            }

            // Also skip onServerError once stopped: some of its branches (loadDoc,
            // fetchUpdates) assume an active session and dereference the now-nulled emitter.
            if (this.syncState !== CollabSessionSyncState.Stopped) {
              this.syncState = CollabSessionSyncState.SaveFailed;
              this.onServerError(error);
            }
            throw error;
          });
        } catch (error) {
          // Update the sync state
          this.syncState = CollabSessionSyncState.SaveFailed;
          return Promise.reject(error);
        }
      }
    } else {
      return Promise.resolve(CollabSessionSaveResult.UnableToSave);
    }
  }

  /** Gets the the doc changes from the server and applies them to the local document. */
  public fetchUpdates() {
    if (!this.emitter.emitting('doc.get')) {
      this.emitter.emit<DocUpdatesRequest, DocUpdatesResponse>('doc.updates', {
        docId: this.docId,
        docVersion: getVersion(this.editorState),
        schemaVersion: this.schema.version,
        timelineDocVersion: this.timelineEnabled ? this.getTimelineDocVersion(this.docId) : undefined
      }, this.retryEmit.bind(this)).then(res => {
        // Update the doc state
        this.readOnly = res.readOnly;
        this.docSize = res.size;
        this.exceedsMaxSize = res.exceedsMaxSize;
        this.exceedsMongoDBMaxSize = res.exceedsMongoDBMaxSize;
        this.docModifiedAt = res.modifiedAt;
        this.docCommittedAt = res.committedAt;

        let tr: Transaction;

        if (this.canReceiveUpdates) {
          // If the local version is older than the authority's version then update the local version
          if (getVersion(this.editorState) < res.docVersion) {
            tr = receiveTransaction(this.editorState, Array.isArray(res.steps) ? res.steps.map(step => Step.fromJSON(this.schema, step)) : [], res.clientIds, { mapSelectionBackward: true });
            if (this.supportOperations)
              tr = this.patchUnconfirmedOperations(tr);
            this.receiveCollabUserTransaction(tr, this.editorState, res.users);
          }
          // Else if there are collab users then update the plugin state to match the collab-authority
          else if (res.users) {
            tr = this.receiveCollabUserTransaction(this.editorState.tr, this.editorState, res.users);
          }
        } else {
          this.hasMissedUpdates = true;
        }

        // Dispatch the transaction to update the plugin states
        if (tr) {
          this.dispatchCollabTransaction(tr, true);
        }
        // Else check if there are more changes to send to the server
        else {
          this.checkForAndSendChanges();
        }

        // Emit the timeline update if it exists in the response
        if (res.timelineUpdate) {
          this.timelineUpdate$.next(res.timelineUpdate);
        }

        // Emit the users update if it exists in the response
        if (res.users) {
          this.usersUpdate$.next(res.users);
        }

        // If the collab session was in error because of a version mismatch then that error has been fixed by this fetching of doc updates
        if (this.error?.type === CollabSessionError.VersionMismatch) {
          this.error = null;
        }
      }).catch(error => {
        this.onServerError(error);
      });
    }
  }

  /** Loads the entire document with the server version. */
  private loadDoc() {
    // Unload the doc and clear any pending emissions
    this.emitter.cancelAllEmissions();
    this.loaded = false;
    this.editorState = null;
    this.syncState = CollabSessionSyncState.NotLoaded;
    // Switch to loading the doc
    this.syncState = CollabSessionSyncState.Loading;

    this.emitter.emit<DocGetRequest, DocGetResponse>('doc.get', {
      docId: this.docId,
      schemaVersion: this.schema.version
    }, this.retryEmit.bind(this)).then(data => {
      // Load the doc into a new editor state
      this.editorState = this.createEditorState(data.doc, data.version, data.users);

      // Update the doc state
      this.readOnly = data.readOnly;
      this.docSize = data.size;
      this.docCreatedAt = data.createdAt;
      this.docCommittedAt = data.committedAt;
      this.docModifiedAt = data.modifiedAt;
      this.exceedsMaxSize = data.exceedsMaxSize;
      this.exceedsMongoDBMaxSize = data.exceedsMongoDBMaxSize;

      const change: DocumentStateChange = {
        state: this.editorState,
        reason: DocumentStateChangeReason.Initialize,
        changed: false
      };
      // Emit the update event so that the editorState gets applied to the editorView
      this.emit('update', {
        session: this,
        change
      });

      // If the collab session was in error because the version was too old or the version was invalid then that error has been fixed by loading the doc
      if (this.error?.type === CollabSessionError.VersionTooOld || this.error?.type === CollabSessionError.DocVersionInvalid) {
        this.error = null;
      }
      this.loaded = true;
      this.syncState = CollabSessionSyncState.Loaded;
      this.emit('load', this);
    }).then(() => {
      this.sendUserData(false);
    }).catch(error => {
      // Set the sync state to failed unless the error was because doc.get was already being requested or because doc.get was canceled
      // These errors aren't considered failures
      if (error.name !== SocketEmitterError.AlreadyEmittingMessage && error.name !== SocketEmitterError.EmissionCanceled) {
        this.syncState = CollabSessionSyncState.LoadFailed;
      }
      this.onServerError(error);
    });
  }

  private sendUserData(checkForChanges: boolean = true): Promise<void> {
    if (!this.userDataEnabled) {
      return;
    }

    if (!this.emitter.emitting('doc.user.update')) {
      const collabUser = this.createCollabUserUpdate(this.editorState);
      return this.emitter.emit<DocUserUpdateRequest>('doc.user.update', collabUser, this.retryEmit.bind(this)).then(() => {
        // Move the plugin state forward to match the collab-authority. receiveCollabUserTransaction only returns a transaction if the states do not already match
        const tr = this.receiveCollabUserTransaction(this.editorState.tr, this.editorState, [collabUser], { action: 'add' });
        if (tr) {
          this.dispatchCollabTransaction(tr, false);
        }

        // Check if there is a new user update to send
        if (checkForChanges && this.hasSendableCollabUserData(this.editorState)) {
          this.throttledSendUserData();
        }

        this.emit('user-data-sent', this);
      }).catch(error => {
        this.onServerError(error);
      });
    }
  }

  private receiveCollabUserTransaction(tr: Transaction, editorState: EditorState, collabUsers: CollabUserJSON[], options?: { action: 'add' | 'set' }): Transaction {
    if (this.userDataEnabled) {
      return receiveCollabUserTransaction(tr, editorState, collabUsers, options);
    }
  }

  private hasSendableCollabUserData(editorState: EditorState): boolean {
    return this.userDataEnabled && hasSendableCollabUser(editorState);
  }

  private createUpdate(state: EditorState, stepsInfo: SendableSteps): DocUpdateRequest {
    return {
      clientId: stepsInfo.clientID as string ?? 'unknown',
      collabUser: this.userDataEnabled ? this.createCollabUserUpdate(state) : undefined,
      docId: this.docId,
      docVersion: getVersion(state),
      schemaVersion: this.schema.version,
      steps: stepsAsJSON(stepsInfo.steps as CollabStep[]),
      userId: this.userId
    };
  }

  private createCollabUserUpdate(state: EditorState): DocUserUpdateRequest {
    return {
      clientId: this.clientId,
      docVersion: getVersion(state),
      selection: state.selection.toJSON(),
      userId: this.userId,
      userName: this.userName
    };
  }

  /** Creates a new state with the proper plugins. */
  private createEditorState(doc: ProseMirrorNodeJSON, docVersion: number, collabUsers: CollabUserJSON[]): EditorState {
    // Include the transaction id and collab plugins
    const plugins = this.plugins.concat(
      transactionIdPlugin(),
      collab({
        version: docVersion,
        clientID: this.clientId
      })
    );

    // Include the collab users plugin if enabled
    if (this.userDataEnabled) {
      plugins.push(collabUsersPlugin({
        clientId: this.clientId,
        collabUsers,
        document: this.document,
        userId: this.userId
      }));
    }

    return EditorState.create({
      doc: this.schema.nodeFromJSON(doc),
      schema: this.schema,
      plugins
    });
  }

  /** Returns true if the SocketEmitter should retry emitting a message when an error occurs. */
  private retryEmit(error: Error): boolean {
    // These errors will be handled by the session and the message should not be re-emitted
    if (error.name === CollabSessionError.SchemaVersionIncompatible ||
      error.name === CollabSessionError.VersionMismatch ||
      error.name === CollabSessionError.VersionTooOld ||
      error.name === CollabSessionError.DocVersionInvalid ||
      error.name === SocketSessionError.AccessDenied ||
      error.name === CollabSessionError.DocLoadFailed ||
      error.name === CollabSessionError.DocSizeTooLarge ||
      error.name === CollabSessionError.MongoDBDocumentSizeTooLarge ||
      error.name === SocketSessionError.BadRequest ||
      error.name === CollabSessionError.DocReadOnly
    ) {
      return false;
    }

    return true;
  }

  /** Handles server errors. */
  private onServerError(error: Error | CollabDocSerializationError) {
    let handled = true;

    if (error.name === CollabSessionError.SchemaVersionIncompatible) {
      // The client and server have incompatible schemas meaning they cannot work together. So just disconnect from the server
      this.stop();
    } else if (error.name === CollabSessionError.VersionMismatch) {
      // The client is not at the same version of the server. So fetch the changes to become up to date with the server
      this.fetchUpdates();
    } else if (error.name === CollabSessionError.VersionTooOld) {
      // The server does not have enough history to bring this document up to date step by step. So instead just reload the entire document from the server
      this.loadDoc();
    } else if (error.name === CollabSessionError.DocVersionInvalid) {
      // The client has somehow moved ahead of the server in version numbers. To restore the client to a working state just reload the entire document from the server
      this.loadDoc();
    } else if (error.name === SocketSessionError.AccessDenied) {
      // The user does not have access to this collaborative document or the document does not exist
      this.stop();
    } else if (error.name === CollabSessionError.DocLoadFailed) {
      // The collab server was unable to load the document
      this.stop();
    } else if (error.name === CollabSessionError.DocSizeTooLarge) {
      // The collab server was unable to load the document because it was too large
      this.docSize = (error as CollabDocSerializationError).docSize;
      this.stop();
    } else if (error.name === CollabSessionError.MongoDBDocumentSizeTooLarge) {
      // The collab server was unable to load the document because it was too large to create in MongoDB
      this.stop();
    } else if (error.name === SocketSessionError.BadRequest) {
      // The collab server was unable to load the document because it received invalid parameters
      this.stop();
    } else if (error.name === CollabSessionError.DocReadOnly) {
      this.readOnly = true;
    } else if (error.name === SocketEmitterError.AlreadyEmittingMessage || error.name === SocketEmitterError.EmissionCanceled) {
      // Do nothing. These errors from the SocketEmitter can happen under normal operations
      handled = false;
    } else {
      handled = false;
    }

    if (handled) {
      this.error = { type: error.name as CollabSessionError, error };
    }
  }

  /**
   * Splits the unconfirmed steps in the transaction into operations,
   * revalidates these operations and returns a new transaction with confirmed operations.
   * */
  private patchUnconfirmedOperations(tr: Transaction): Transaction {
    const collabMeta = tr.getMeta(collabPluginKey);
    const unconfirmedStepsCount = collabMeta?.unconfirmed?.length ?? 0;
    // if there are no unconfirmed changes
    if (!unconfirmedStepsCount) {
      // mark the transaction as a transaction with confirmed operations
      // since all document changes received from the server are confirmed
      tr.setMeta(operationPluginKey, { hasConfirmedOperations: true });
      // return the transaction as is (without patching its steps)
      return tr;
    }

    // get unconfirmed steps
    const unconfirmedSteps = tr.steps.slice(- unconfirmedStepsCount);
    // parse steps as operations
    const operations = parseOperations(unconfirmedSteps);
    // if steps can not be parsed as operations (because these could be code changes, for example)
    if (!operations) {
      // mark the transaction as a transaction with confirmed operations
      // since all document changes received from the server are confirmed
      tr.setMeta(operationPluginKey, { hasConfirmedOperations: true });
      // return the transaction as is (without patching its steps)
      return tr;
    }

    // create a transaction for confirmed operations
    const confirmedTransaction = this.editorState.tr;
    // mark the transaction as a draft transaction with confirmed operations
    const meta: TransactionMeta = { draft: true, hasConfirmedOperations: true };
    confirmedTransaction.setMeta(operationPluginKey, meta);

    // get confirmed steps from original transaction: reverted local steps and incoming collab steps
    const confirmedStepsCount = tr.steps.length - unconfirmedStepsCount;
    const confirmedSteps = tr.steps.slice(0, confirmedStepsCount);
    // add confirmed steps to the transaction
    confirmedSteps.forEach(step => confirmedTransaction.step(step));

    // get an intermediate editor state with confirmed operations
    let confirmedState = this.editorState.apply(confirmedTransaction);

    let rejectedOperationsCount = 0;
    const rejectedStepsMapping = new Mapping();
    const unconfirmed = [];

    // test each operation
    operations.forEach(op => {
      // get operation steps taking into account the previously rejected operation steps
      const steps = unconfirmedSteps.slice(op.firstStepIndex, op.lastStepIndex + 1).map(step => step.map(rejectedStepsMapping)).filter(step => step !== null);
      // create a test transaction to validate operation
      const testTransaction = confirmedState.tr;
      // mark the transaction as a draft transaction
      testTransaction.setMeta(operationPluginKey, { draft: true });
      steps.forEach(step => {
        testTransaction.step(step);
      });
      // execute the test transaction with the current editor state
      const result = confirmedState.applyTransaction(testTransaction);
      // if the test transaction is applied
      if (result.transactions.length) {
        // accept the current operation
        steps.forEach((step, index) => {
          confirmedTransaction.step(step);
          const unconfirmedStepIndex = op.firstStepIndex + index;
          const rebaseable = { step: step, inverted: step.invert(testTransaction.docs[index]), origin: collabMeta.unconfirmed[unconfirmedStepIndex].origin };
          unconfirmed.push(rebaseable);
        });
        // set the new editor state as a current confirmed editor state to use for validating the next operation
        confirmedState = result.state;
      } else {
        rejectedOperationsCount++;
        // process steps of the rejected operation to update mapping for steps from next operations
        steps.forEach(step => rejectedStepsMapping.appendMap(step.getMap().invert()));
      }
    });

    // if all unconfirmed steps are accepted
    if (unconfirmed.length === unconfirmedStepsCount) {
      // mark the transaction as a transaction with confirmed operations
      tr.setMeta(operationPluginKey, { hasConfirmedOperations: true });
      // return the transaction as is (without patching its steps)
      return tr;
    }

    // update transaction meta
    (confirmedTransaction as any).meta = (tr as any).meta;
    confirmedTransaction.setMeta('rebased', unconfirmed.length);
    collabMeta.unconfirmed = unconfirmed;
    // we finished test mode, so now the transaction for production
    meta.draft = false;

    // return a new transaction with patched operation steps
    return confirmedTransaction;
  }
}
